/**
 * Utilities for handling image URLs in LaTeX source.
 *
 * Long storage URLs (e.g. http://localhost:5001/api/documents/.../assets/...)
 * make LaTeX previews unreadable. These helpers replace them with short
 * `figures/filename.ext` paths (what you'd use in a real .tex project) and
 * can restore the originals when the user explicitly asks for them.
 */

const INCLUDEGRAPHICS_RE =
  /\\includegraphics(\[[^\]]*\])\{([^}]+)\}/g;

/** Extract a short display filename from a URL or path. */
function extractFilename(src: string): string {
  try {
    const url = new URL(src);
    // For API asset URLs like /api/documents/.../assets/uuid, use the last segment
    const segments = url.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "image";
    // If the last segment looks like a UUID (no extension), add .png
    if (!last.includes(".")) {
      return `${last.substring(0, 8)}.png`;
    }
    return last;
  } catch {
    // Not a URL — might already be a filename or relative path
    const parts = src.split("/");
    return parts[parts.length - 1] || "image.png";
  }
}

export interface ImageMapping {
  clean: string;   // figures/filename.ext
  original: string; // full URL
}

/**
 * Replace long image URLs in \includegraphics{} with clean `figures/filename`
 * paths. Returns the cleaned LaTeX and a mapping so the originals can be
 * restored.
 */
export function cleanImageUrls(latex: string): {
  cleaned: string;
  mappings: ImageMapping[];
} {
  const mappings: ImageMapping[] = [];
  let counter = 0;

  const cleaned = latex.replace(INCLUDEGRAPHICS_RE, (_match, options, src) => {
    // Only replace URLs and long paths (short filenames are fine)
    if (src.length <= 40 && !src.startsWith("http") && !src.startsWith("/api")) {
      return `\\includegraphics${options}{${src}}`;
    }

    counter++;
    const filename = extractFilename(src);
    const cleanPath = counter > 1 ? `figures/${filename}` : `figures/${filename}`;

    mappings.push({ clean: cleanPath, original: src });
    return `\\includegraphics${options}{${cleanPath}}`;
  });

  return { cleaned, mappings };
}

/**
 * Restore original image URLs in cleaned LaTeX using the mapping.
 */
export function restoreImageUrls(
  cleanedLatex: string,
  mappings: ImageMapping[],
): string {
  let result = cleanedLatex;
  for (const { clean, original } of mappings) {
    result = result.replace(
      `\\includegraphics`,
      `\\includegraphics`,
    );
    // Replace the clean path with the original
    result = result.replace(`{${clean}}`, `{${original}}`);
  }
  return result;
}

/**
 * Strip all \includegraphics lines from LaTeX (for "copy without images").
 * Also removes the surrounding \begin{figure}...\end{figure} environments.
 */
export function stripImages(latex: string): string {
  // Remove entire figure environments
  const figureRe = /\\begin\{figure\}[\s\S]*?\\end\{figure\}\n?/g;
  return latex.replace(figureRe, "").replace(/\n{3,}/g, "\n\n");
}
